// Payments list

import { useMemo, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import Select from './Select';
import ManualPay from './ManualPay';
import PaymentsListPDF from './PaymentsListPDF';
import Spinner from './Spinner';
import LoadMoreSentinel from './LoadMoreSentinel';
import moment from 'moment';
import { capFirstLetter } from '../utils/misc';
import { usePaymentsPage, useTeamsDirectory } from '../utils/hooks';
import { exportPaymentsCsv } from '../utils/paymentsCsv';
import { PaymentPurposes, PaymentMethods, Currencies } from '../utils/enums';
import { useFirebase } from './FirebaseContextProvider';
import { useModal } from './ModalProvider';
import './PaymentsList.scss';

// Flat rows the grid, the PDF and the CSV all read, so the three cannot drift
// apart. Team and tournament are resolved because the ids say nothing in an
// exported file; `time` is missing until the server resolves the
// serverTimestamp of a just-written payment
const toRows = (items, teamsById, tournamentsById) =>
  items.map(({ id, purpose, details, time, method, amount, currency }) => {
    const date = time && moment(time.toDate());

    return {
      id,
      date: date ? date.format('YYYY-MM-DD') : '',
      dateLabel: date ? date.format('MMM D, YYYY') : '',
      method: capFirstLetter(method),
      purpose,
      teamName: teamsById.get(details?.team)?.name ?? '',
      tournamentName: tournamentsById.get(details?.tournament)?.name ?? '',
      amount,
      currency: currency ?? Currencies.USD,
    };
  });

export default function PaymentsList() {
  const { tournamentsById, userIsAdmin, isUserResolved } = useFirebase();
  // A page of 50 payments names up to 50 teams; the whole directory is six
  // documents, and registering that many ids by hand would cost more
  const { teamsById } = useTeamsDirectory();
  const { openModal } = useModal();
  const pdfRef = useRef(null);

  // Build options for method and time filters
  const methods = ['All methods', ...Object.values(PaymentMethods)];
  const times = [
    'All time',
    ...(() => {
      const monthsList = [];
      for (let i = 0; i < 12; i++) {
        monthsList.push(moment().subtract(i, 'months').format('MMMM YYYY'));
      }
      return monthsList;
    })(),
  ];
  // Local state for selected filters
  const [selectedMethod, setSelectedMethod] = useState(methods[0]);
  const [selectedTime, setSelectedTime] = useState(times[0]);

  // Both filters go into the query: the list is paginated, and filtering in
  // memory would only ever match the pages already read
  const { items, isLoading, isLoadingMore, hasMore, loadMore } =
    usePaymentsPage({
      method: selectedMethod === methods[0] ? null : selectedMethod,
      period: selectedTime === times[0] ? null : selectedTime,
    });

  const rows = useMemo(
    () => toRows(items, teamsById, tournamentsById),
    [items, teamsById, tournamentsById],
  );

  // Both exports carry the filters they were taken under, so two files from
  // the same tab never look interchangeable. The heading names the selection
  // and nothing else — beside a period, a second date reads as part of it
  const reportTitle = [
    'Payments',
    selectedMethod === methods[0] ? null : capFirstLetter(selectedMethod),
    selectedTime === times[0] ? null : selectedTime,
  ]
    .filter(Boolean)
    .join(' - ');

  const now = moment();
  const fileName = `${reportTitle} - ${now.format('YYYY-MM-DD')}`;
  const generatedAt = now.format('MMM D, YYYY, h:mm A');

  const onPrintPDF = useReactToPrint({
    contentRef: pdfRef,
    documentTitle: fileName,
  });

  return (
    <>
      <div className='payments-list'>
        <div className='sticky-top'>
          <div className='controls'>
            <div className='left'>
              <Select
                options={methods}
                value={selectedMethod}
                onChange={setSelectedMethod}
                placeholder='Method'
                small
              />
              <Select
                options={times}
                value={selectedTime}
                onChange={setSelectedTime}
                placeholder='Period'
                small
              />
            </div>
            <div className='right'>
              {userIsAdmin && (
                <>
                  <button
                    className='bw small'
                    onClick={() =>
                      openModal({
                        header: 'Zelle Pay',
                        body: <ManualPay method={PaymentMethods.ZELLE} />,
                      })
                    }
                  >
                    Zelle Pay
                  </button>
                  <button
                    className='bw small'
                    onClick={() =>
                      openModal({
                        header: 'Venmo Pay',
                        body: <ManualPay method={PaymentMethods.VENMO} />,
                      })
                    }
                  >
                    Venmo Pay
                  </button>
                  <button
                    className='bw small'
                    onClick={() =>
                      openModal({
                        header: 'Cash Pay',
                        body: <ManualPay method={PaymentMethods.CASH} />,
                      })
                    }
                  >
                    Cash Pay
                  </button>
                </>
              )}
              <button
                className='bw small fa-pdf icon'
                onClick={onPrintPDF}
                disabled={!rows.length}
              >
                Print/Save as PDF
              </button>
              <button
                className='bw small fa-csv icon'
                onClick={() => exportPaymentsCsv(`${fileName}.csv`, rows)}
                disabled={!rows.length}
              >
                Save as CSV
              </button>
            </div>
          </div>
          <ul className='header tab-header'>
            <li className='col-1'>Date</li>
            <li className='col-15'>Method</li>
            <li className='col-2'>ID</li>
            <li className='col-12'>Date, Method, ID</li>
            <li className='col-3'>Description</li>
            <li className='col-4'>Amount</li>
          </ul>
        </div>
        {rows.map((payment) => {
          let description = '';
          const {
            id,
            purpose,
            teamName,
            tournamentName,
            dateLabel,
            method,
            amount,
          } = payment;
          switch (purpose) {
            case PaymentPurposes.TOURNAMENT:
              description = (
                <>
                  For the <span className='font-medium'>{teamName}</span> team
                  to participate in the{' '}
                  <span className='font-medium'>{tournamentName}</span>{' '}
                  tournament
                </>
              );
              break;
            case PaymentPurposes.CARD:
              description = "Player's card";
              break;
            default:
              break;
          }

          return (
            <div className='payment' key={id}>
              <p className='col-1'>{dateLabel}</p>
              <p className='col-15'>{method}</p>
              <p className='col-2'>{id}</p>
              <div className='col-12'>
                <p>{dateLabel}</p>
                <p>{method}</p>
                <p className='id'>{id}</p>
              </div>
              <p className='col-3'>{description}</p>
              <p className='col-4 font-medium'>{'$' + amount}</p>
            </div>
          );
        })}
        <LoadMoreSentinel
          hasMore={hasMore}
          isLoading={isLoadingMore}
          onLoadMore={loadMore}
        />
        {/* No role yet means no query yet — without this the tab renders as
            "no payments" for the frames before the self document lands */}
        <Spinner show={isLoading || !isUserResolved} />
      </div>
      {/* Outside .payments-list: that box sets overflow: clip, which would
          clip an offscreen node parked inside it */}
      <div ref={pdfRef} className='payments-list-pdf-wrap'>
        <PaymentsListPDF
          rows={rows}
          title={reportTitle}
          generatedAt={generatedAt}
        />
      </div>
    </>
  );
}
